/**
 * Share quota (plan Task 7.16): the current quota and usage, what a change means for the bill (`billingSentence`),
 * the model's bounds; Save sends the quota the user saw as `expectedQuotaGiB` (main compares before every attempt).
 * A `QuotaChanged` conflict keeps the draft and offers Reload (the re-read quota becomes the next expected one); the
 * 24-hour provisioned cooldown shows its sentence. Entra administration is attempted (I2). TanStack Form; one save in
 * flight; closing while it runs cancels it in main.
 */
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { billingSentence, quotaProblem } from '../../../../../core/engines/azureStorageShareQuota';
import type { FileShareBilling, StorageOperationError } from '../../../../../types/azureStorage';
import { formatStorageSize, formatStorageTimestamp } from '../../utils/storageItemRows';
import type { ShareDialogRequest } from '../fileDialogStore';
import { startShareRequest } from '../shareApi';
import { shareActionOutcome, trackShareAction } from '../shareActions';
import { quotaRangeText, useFileAccountFacts } from './fileAccountFacts';
import { ShareErrorNotice } from './ShareErrorNotice';
import { useShareRefusal } from './shareGate';
import { useShareOperation } from './useShareOperation';
import { useShareRead } from './useShareRead';

type Props = { request: ShareDialogRequest; onClose: () => void };

export function ShareQuotaDialog({ request, onClose }: Props) {
  const share = request.resource?.share ?? '';
  const live = { kind: 'share', share } as const;
  const facts = useFileAccountFacts(request.connectionId);
  const refusal = useShareRefusal(request.connectionId)('set-quota', { snapshot: request.resource?.snapshot });
  const read = useShareRead({
    connectionId: request.connectionId, resource: live, purpose: 'properties', enabled: !refusal,
    start: () => startShareRequest('shareProperties', { connectionId: request.connectionId, resource: live }),
  });
  const properties = read.data?.properties;
  const billing: FileShareBilling = properties?.billing && properties.billing !== 'unknown' ? properties.billing : facts.billing;
  const model = { billing, largeFileShares: facts.largeFileShares };
  const protocol = properties?.protocol ?? request.protocol ?? 'unknown';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<StorageOperationError>();
  const { begin, settle, abandon, isActive } = useShareOperation();
  const form = useForm({
    defaultValues: { quota: '' },
    onSubmit: async ({ value }) => {
      const quotaGiB = Number(value.quota.trim());
      const expectedQuotaGiB = properties?.quotaGiB;
      if (isActive() || refusal || expectedQuotaGiB === undefined || quotaProblem(quotaGiB, model)) return;
      const meta = { source: request.source, protocol };
      setPending(true);
      setError(undefined);
      const handle = startShareRequest('shareQuota', { connectionId: request.connectionId, resource: live, quotaGiB, expectedQuotaGiB });
      begin(handle, 'quota', meta);
      const result = await handle.promise;
      if (!settle(handle)) return;
      setPending(false);
      trackShareAction('quota', shareActionOutcome(result.ok ? undefined : result.error), meta);
      if (result.ok) onClose();
      else setError(result.error);
    },
  });
  // The draft starts at the quota read once; a Reload keeps whatever the user typed.
  const loadedQuota = properties?.quotaGiB;
  useEffect(() => {
    if (loadedQuota !== undefined && form.getFieldValue('quota') === '') form.setFieldValue('quota', String(loadedQuota));
  }, [loadedQuota, form]);
  const reload = () => { setError(undefined); read.reload(); };
  const dismiss = () => { abandon(); onClose(); };
  const sentence = billingSentence(billing);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent data-testid="share-quota-dialog">
        <DialogHeader>
          <DialogTitle>Quota of {share}</DialogTitle>
          <DialogDescription>The largest size the share may grow to.</DialogDescription>
        </DialogHeader>
        {refusal && <p className="text-sm text-muted-foreground" data-testid="share-quota-refusal">{refusal}</p>}
        {read.isLoading && <p className="text-sm text-muted-foreground" data-testid="share-quota-loading">Reading the share…</p>}
        {read.error && <ShareErrorNotice error={read.error} testId="share-quota-read-error" />}
        {properties && (
          <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }}>
            <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">Current quota</dt>
              <dd data-testid="share-quota-current">{properties.quotaGiB === undefined ? 'Unknown' : `${properties.quotaGiB.toLocaleString('en-US')} GiB`}</dd>
              <dt className="text-muted-foreground">Used</dt>
              <dd data-testid="share-quota-usage">{properties.usageBytes === undefined ? 'Unknown' : formatStorageSize(properties.usageBytes)}</dd>
            </dl>
            {sentence && <p className="text-xs text-muted-foreground" data-testid="share-quota-billing">{sentence}</p>}
            {properties.nextQuotaDowngradeAt && <p className="text-xs text-muted-foreground" data-testid="share-quota-next-downgrade">The provisioned size can be lowered again after {formatStorageTimestamp(properties.nextQuotaDowngradeAt)}.</p>}
            <form.Field name="quota">{(field) => {
              const problem = field.state.value.trim() ? quotaProblem(Number(field.state.value.trim()), model) : undefined;
              return (
                <div className="space-y-1">
                  <Label htmlFor="share-quota-input">New quota (GiB)</Label>
                  <Input id="share-quota-input" inputMode="numeric" value={field.state.value} data-testid="share-quota-input" onChange={(event) => field.handleChange(event.target.value)} />
                  <p className="text-xs text-muted-foreground" data-testid="share-quota-range">{quotaRangeText(model)}</p>
                  {problem && <p className="text-xs text-destructive" data-testid="share-quota-problem">{problem}</p>}
                </div>
              );
            }}</form.Field>
            {error && <ShareErrorNotice error={error} testId="share-quota-error" onReload={reload} />}
            <form.Subscribe selector={(state) => state.values.quota}>{(quota) => {
              const value = Number(quota.trim());
              const invalid = !quota.trim() || Boolean(quotaProblem(value, model)) || value === properties.quotaGiB || properties.quotaGiB === undefined;
              return (
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={dismiss} data-testid="share-quota-cancel">Cancel</Button>
                  <ReasonButton type="submit" reason={refusal} disabled={pending || invalid} data-testid="share-quota-submit">
                    {pending && <RandomDotmSpinner size={14} className="mr-1" />}Save quota
                  </ReasonButton>
                </DialogFooter>
              );
            }}</form.Subscribe>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
